import {
  Check,
  FileText,
  Image,
  Loader2,
  Paperclip,
  Video,
} from 'lucide-react';
import React from 'react';

import { cn } from '@/lib/utils';
import { CommunicationAttachment } from '@/services/types/client-communications-types';

import { useClientCommunications } from '../ClientCommunicationsContext';

const getFileIcon = (contentType: string) => {
  if (contentType.startsWith('image/')) {
    return Image;
  }
  if (contentType.startsWith('video/')) {
    return Video;
  }
  return FileText;
};

const formatFileSize = (bytes: number) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

interface AttachmentCardProps {
  attachment: CommunicationAttachment;
  isSelected: boolean;
  onToggleSelect: () => void;
}

const AttachmentCard: React.FC<AttachmentCardProps> = ({
  attachment,
  isSelected,
  onToggleSelect,
}) => {
  const FileIcon = getFileIcon(attachment.content_type);
  const isImage = attachment.content_type.startsWith('image/');

  return (
    <div
      className={cn(
        'relative group rounded-lg border overflow-hidden cursor-pointer transition-all',
        isSelected
          ? 'border-primary ring-1 ring-primary/20'
          : 'border-zinc-200 hover:border-zinc-300',
      )}
      onClick={onToggleSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onToggleSelect();
        }
      }}
      role="checkbox"
      aria-checked={isSelected}
      tabIndex={0}
    >
      {/* Thumbnail or icon */}
      <div className="aspect-square bg-zinc-50 flex items-center justify-center overflow-hidden">
        {isImage ? (
          <img
            src={attachment.thumbnail_url || attachment.download_url}
            alt={attachment.filename}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Hide broken image and show icon fallback
              const target = e.target as HTMLImageElement;
              target.style.display = 'none';
              target.nextElementSibling?.classList.remove('hidden');
            }}
          />
        ) : null}
        {/* Icon fallback - shown for non-images or when image fails to load */}
        <FileIcon
          className={cn('h-6 w-6 text-zinc-400', isImage && 'hidden')}
        />
      </div>

      {/* Selection indicator */}
      <div
        className={cn(
          'absolute top-1.5 right-1.5 h-4 w-4 rounded-full border flex items-center justify-center transition-all',
          isSelected
            ? 'bg-primary border-primary'
            : 'bg-white/80 border-zinc-300 opacity-0 group-hover:opacity-100',
        )}
      >
        {isSelected && <Check className="h-2.5 w-2.5 text-white" />}
      </div>

      {/* File info */}
      <div className="p-2 bg-white border-t border-zinc-100">
        <p className="text-[11px] font-semibold text-zinc-900 truncate">
          {attachment.filename}
        </p>
        <p className="text-[10px] text-zinc-500 font-medium">
          {formatFileSize(attachment.size_bytes)}
        </p>
      </div>
    </div>
  );
};

export const AttachmentsView: React.FC = () => {
  const {
    attachments,
    isLoadingAttachments,
    selectedAttachments,
    toggleAttachmentSelection,
  } = useClientCommunications();

  if (isLoadingAttachments) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px]">
        <Loader2 className="h-5 w-5 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (attachments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full min-h-[200px] text-center px-4">
        <div className="w-10 h-10 rounded-full bg-zinc-200 flex items-center justify-center mb-3">
          <Paperclip className="h-5 w-5 text-zinc-500" />
        </div>
        <p className="text-sm font-semibold text-zinc-800">No attachments</p>
        <p className="text-xs text-zinc-500 font-medium mt-1">
          Shared files will appear here
        </p>
      </div>
    );
  }

  return (
    <div className="px-3 py-3">
      {/* Selection count */}
      {selectedAttachments.size > 0 && (
        <p className="text-[11px] text-zinc-600 font-semibold mb-2 px-1">
          {selectedAttachments.size} selected
        </p>
      )}

      {/* Attachment grid */}
      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
        {attachments.map((attachment) => (
          <AttachmentCard
            key={attachment.id}
            attachment={attachment}
            isSelected={selectedAttachments.has(attachment.id)}
            onToggleSelect={() => toggleAttachmentSelection(attachment.id)}
          />
        ))}
      </div>
    </div>
  );
};

export default AttachmentsView;
